// src/Components/modules/Coordinacion/ItemTraslado.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, Typography, Space, Row, Col, Badge, Tag, Tooltip, Checkbox } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  CarOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CarFilled,
  InfoOutlined,
  PhoneOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { format, addHours } from "date-fns";
import StatusTag from "../../common/StatusTag";
import ColoredCard from "../../common/ColoredCard";
import { formatCurrencyCOP } from "../../../utils/formatCurrency";
import ObservacionesDialog from "../../modules/Dialog/ObservacionesDialog";

const { Text } = Typography;

// Función para ajustar la fecha según la zona horaria
const ajustarFecha = (fecha) => {
  return addHours(new Date(fecha), 5);
};

// Funciones para obtener colores según el estado
const getStatusColor = (status) => {
  const statusColors = {
    Cancelado: "#b10e1a",
    Finalizado: "#4d4d4d",
    Fallido: "#4a2c04",
    FallidoXvalidar: "#d4380d",
    FinalizadoXvalidar: "#722ED1", // Púrpura - Pendiente de validación OTP
    Asignado: "#1f7362",
    "Pre-Asignado": "#0c27a0",
    Programado: "#6c06b5",
    Enterado: "#0c5b07",
    Iniciado: "#fa7114",
    Aceptado: "#088A08",
    "En Camino": "#FFBF00",
    Esperando: "#a44706",
    default: "#b0b500",
  };
  return statusColors[status] || statusColors.default;
};

const getConfirmacionColor = (confirmacion) => {
  return confirmacion === "Confirmado" ? "#52c41a" : "#f5222d"; // Verde si está confirmado, rojo si no
};

const getSeguimientoColor = (seguimiento) => {
  return seguimiento === "Validado" ? "#52c41a" : "#f5222d"; // Verde si validado, rojo si no
};

const getInicioStatusColor = (inicioStatus) => {
  const inicioStatusColors = {
    "Inicio Exitosamente": "#52c41a", // Verde
    "Ubicación No Válida para Iniciar": "#faad14", // Amarillo
    "Sin Conectividad Para Iniciar": "#faad14", // Amarillo
  };
  return inicioStatusColors[inicioStatus] || "#faad14"; // Por defecto amarillo
};

const getFinStatusColor = (finStatus) => {
  const finStatusColors = {
    "Finalizó Exitosamente": "#52c41a", // Verde
    "Ubicación No Válida para Finalizar": "#faad14", // Amarillo
    "Sin Conectividad Para Finalizar": "#faad14", // Amarillo
  };
  return finStatusColors[finStatus] || "#faad14"; // Por defecto amarillo
};

const ItemTraslado = ({ traslado, handleContextMenu, regimenes, seleccionable = false, seleccionado = false, onToggleSeleccion }) => {
  const statusColor = getStatusColor(traslado.status);
  const confirmacionColor = getConfirmacionColor(traslado.confirmacion);
  const inicioStatusColor = getInicioStatusColor(traslado.inicio_status);
  const finStatusColor = getFinStatusColor(traslado.fin_status);
  const [observacionesOpen, setObservacionesOpen] = useState(false);

  // Función para manejar la apertura del modal
  const handleOpenObservaciones = (e) => {
    e.stopPropagation();
    setObservacionesOpen(true);
  };

  // Función para manejar el cierre del modal
  const handleCloseObservaciones = () => {
    setObservacionesOpen(false);
  };

  return (
    <ColoredCard color={statusColor}>
      <Card
        className="traslados-item"
        key={traslado.id}
        onContextMenu={(event) => handleContextMenu(event, traslado)} // Manejador de menú contextual
        hoverable
        variant="borderless" // Desactiva el borde para evitar doble borde
        style={{ padding: 0 }} // Eliminar padding predeterminado de Card
      >
        {/* Contenedor de CS-id y estado */}
        <div
          className="traslados-item-id-container"
          style={{
            backgroundColor: "#0a2e5c", // Color neutro oscuro
            padding: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            flexWrap: "wrap", // Permitir envoltura en pantallas pequeñas
          }}
        >
          <Space>
            {seleccionable && (
              <Checkbox
                checked={seleccionado}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSeleccion && onToggleSeleccion(traslado.id)}
                aria-label={`Seleccionar traslado ${traslado.id}`}
              />
            )}
            <Text
              strong
              style={{
                color: "#fff",
                marginRight: "8px",
                wordBreak: "break-all",
              }}
            >
              CS-{traslado.id}
            </Text>
          </Space>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {/* Tag TAI - Solo se muestra si es traslado TAI */}
            {traslado.es_tai && (
              <Tooltip
                title={
                  <div>
                    <div style={{ fontWeight: 'bold' }}>Traslado TAI</div>
                    <div>Tipo: {traslado.tai_trayecto_tipo || 'Sin definir'}</div>
                    {traslado.transportadora_nombre && (
                      <div>Transportadora: {traslado.transportadora_nombre}</div>
                    )}
                    {traslado.es_mogotax_especial && (
                      <div style={{ color: '#faad14', fontWeight: 'bold' }}>
                        Mogotax Especial
                      </div>
                    )}
                  </div>
                }
              >
                <Tag
                  color={traslado.es_mogotax_especial ? "gold" : "purple"}
                  icon={<CarOutlined />}
                  style={{
                    margin: 0,
                    fontWeight: traslado.es_mogotax_especial ? 'bold' : 'normal'
                  }}
                >
                  TAI{traslado.es_mogotax_especial ? ' ★' : ''}
                </Tag>
              </Tooltip>
            )}

            {/* Tag de Estado del Traslado */}
            <StatusTag color={statusColor} text={traslado.status} />

            {/* Tag de Confirmación del Traslado con Icono */}
            <StatusTag
              color={confirmacionColor}
              text={
                traslado.confirmacion === "Confirmado"
                  ? "Confirmado"
                  : "Sin Confirmar"
              }
              icon={
                traslado.confirmacion === "Confirmado" ? (
                  <CheckCircleOutlined />
                ) : null
              }
            />

            {/* Tag de Inicio Status - Solo se muestra si existe inicio_status */}
            {traslado.inicio_status && (
              <StatusTag
                color={inicioStatusColor}
                text={traslado.inicio_status}
              />
            )}

            {/* Tag de Fin Status - Solo se muestra si existe fin_status */}
            {traslado.fin_status && (
              <StatusTag color={finStatusColor} text={traslado.fin_status} />
            )}

            {/* Tag de Seguimiento */}
            <StatusTag
              color={getSeguimientoColor(traslado.seguimiento)}
              text={
                traslado.seguimiento === "Validado" ? "Validado" : "Sin Validar"
              }
              icon={
                traslado.seguimiento === "Validado" ? (
                  <CheckCircleFilled />
                ) : null
              }
            />
          </div>
        </div>

        {/* Distribución de 3 columnas */}
        <Row gutter={[16, 16]} style={{ padding: "10px" }}>
          {/* Columna izquierda: Fechas y horas */}
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size="small">
              <Space>
                <ClockCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>
                  {format(ajustarFecha(traslado.fecha), "dd/MM/yyyy")}
                </Text>
              </Space>
              <Space>
                <ClockCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>Hora Recogida: {traslado.hora_recogida?.slice(0, 5) || '--:--'}</Text>
              </Space>
              <Space>
                <ClockCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>Hora Llegada: {traslado.hora_llegada?.slice(0, 5) || '--:--'}</Text>
              </Space>
            </Space>
          </Col>

          {/* Columna central: Origen y destino */}
          <Col xs={24} sm={12} md={8} style={{ textAlign: "center" }}>
            <Space direction="vertical" size="small">
              <Space>
                <EnvironmentOutlined
                  style={{
                    fontSize: "18px",
                    color: traslado.origen_lugar ? "#52c41a" : "#cccccc",
                    filter: traslado.origen_lugar
                      ? "drop-shadow(0 0 2px rgba(82, 196, 26, 0.4))"
                      : "none",
                  }}
                />{" "}
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {traslado.origen_lugar}
                  </span>{" "}
                  {traslado.origen_direccion} - {traslado.origen_ciudad}
                </Text>
              </Space>
              <Space>
                <EnvironmentOutlined
                  style={{
                    fontSize: "18px",
                    color: traslado.destino_lugar ? "#f5222d" : "#cccccc",
                    filter: traslado.destino_lugar
                      ? "drop-shadow(0 0 2px rgba(245, 34, 45, 0.4))"
                      : "none",
                  }}
                />{" "}
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {traslado.destino_lugar}
                  </span>{" "}
                  {traslado.destino_direccion} - {traslado.destino_ciudad}
                </Text>
              </Space>
            </Space>
          </Col>

          {/* Columna derecha: Información del paciente */}
          <Col xs={24} sm={24} md={8} style={{ textAlign: "right" }}>
            <Space direction="vertical" size="small">
              <Space>
                <UserOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
                <Text>{traslado.nombre_paciente}</Text>
                <Badge
                  count={traslado.observaciones_count || 0}
                  style={{
                    backgroundColor: (traslado.observaciones_count || 0) > 0 ? "#52c41a" : "#d9d9d9",
                    boxShadow: (traslado.observaciones_count || 0) > 0 
                      ? "0 0 0 2px rgba(82, 196, 26, 0.2)" 
                      : "0 0 0 2px rgba(217, 217, 217, 0.2)",
                    cursor: "pointer",
                  }}
                  title={`${traslado.observaciones_count || 0} observaciones`}
                >
                  <CommentOutlined
                    style={{
                      fontSize: "18px",
                      color: (traslado.observaciones_count || 0) > 0 ? "#1890ff" : "#8c8c8c",
                      filter: (traslado.observaciones_count || 0) > 0 
                        ? "drop-shadow(0 0 2px rgba(24, 144, 255, 0.2))" 
                        : "none",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={handleOpenObservaciones}
                  />
                </Badge>
              </Space>
              <Space>
                <Text style={{ color: "#8c8c8c" }}>
                  {traslado.tipo_documento_paciente}
                </Text>
              </Space>
              <Space>
                <IdcardOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>{traslado.numero_documento_paciente}</Text>
              </Space>
              {traslado.numero_contacto_paciente && (
                <Space>
                  <PhoneOutlined
                    style={{ fontSize: "16px", color: "#1890ff" }}
                  />
                  <Text>{traslado.numero_contacto_paciente}</Text>
                </Space>
              )}
            </Space>
          </Col>
        </Row>

        {/* Empresa y Copago */}
        <Row
          gutter={[16, 16]}
          style={{ padding: "0 10px", textAlign: "center" }}
        >
          <Col
            xs={24}
            sm={12}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <MedicineBoxOutlined
              style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
            />
            <Text>
              {traslado.empresa}
              {traslado.regimen_id && (
                <>
                  {" "}
                  <span style={{ fontWeight: "bold" }}> - </span>
                  {regimenes.find((r) => r.id === traslado.regimen_id)
                    ?.nombre || ""}
                </>
              )}
            </Text>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <DollarOutlined
              style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
            />
            <Text>{formatCurrencyCOP(traslado.copago_diario) || "$ 0,00"}</Text>
          </Col>
        </Row>

        {/* Nueva sección para Autorizaciones */}
        <Row
          gutter={[16, 16]}
          style={{ padding: "0 10px", textAlign: "center", marginTop: "10px" }}
        >
          {traslado.autorizacion && (
            <Col xs={24} sm={12} md={12}>
              <Space>
                <InfoCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>
                  <strong>Número de Autorización:</strong>{" "}
                  {typeof traslado.autorizacion === 'string' 
                    ? traslado.autorizacion 
                    : traslado.autorizacion?.numero_autorizacion || 'N/A'}
                </Text>
              </Space>
            </Col>
          )}
          {traslado.preautorizacion && (
            <Col xs={24} sm={12} md={12}>
              <Space>
                <InfoCircleOutlined
                  style={{ fontSize: "16px", color: "#1890ff" }}
                />
                <Text>
                  <strong>Número de Preautorización:</strong>{" "}
                  {typeof traslado.preautorizacion === 'string'
                    ? traslado.preautorizacion
                    : traslado.preautorizacion?.numero_preautorizacion || 'N/A'}
                </Text>
              </Space>
            </Col>
          )}
        </Row>

        {/* Trayecto, duración, distancia y observaciones */}
        <Row
          gutter={[16, 16]}
          style={{ padding: "0 10px", textAlign: "center" }}
        >
          <Col xs={24} sm={12} md={8}>
            <Space>
              <CarOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
              <Text> {traslado.trayecto}</Text>
            </Space>
          </Col>
          {/*traslado.duracion_estimada &&
            Number(traslado.duracion_estimada) > 0 && (
              <Col xs={24} sm={12} md={8}>
                <Space>
                  <ClockCircleOutlined />
                  <Text>{traslado.duracion_estimada} min</Text>
                </Space>
              </Col>
            )}
          {traslado.distancia_estimada && (
            <Col xs={24} sm={12} md={8}>
              <Space>
                <CarFilled />
                <Text>{traslado.distancia_estimada} km</Text>
              </Space>
            </Col>
          )*/}
          {traslado.observaciones_traslado && (
            <Col xs={24} sm={24} md={24}>
              <Space>
                <InfoCircleOutlined
                  style={{ fontSize: "16px", color: "#faad14" }}
                />
                <Text style={{ color: "orange" }}>
                  {traslado.observaciones_traslado}
                </Text>
              </Space>
            </Col>
          )}
          {traslado.indicaciones_conductor && (
            <Col xs={24} sm={24} md={24}>
              <Space>
                <InfoOutlined style={{ fontSize: "16px", color: "#faad14" }} />
                <Text style={{ color: "orange" }}>
                  {traslado.indicaciones_conductor}
                </Text>
              </Space>
            </Col>
          )}
        </Row>

        {/* Tipo de traslado, vehículo y placa */}
        <div
          className="traslados-item-footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap", // Permitir envoltura en pantallas pequeñas
            marginTop: "10px",
            padding: "10px",
          }}
        >
          {/* Tipo de viaje en el lado izquierdo */}
          <Text
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "5px",
            }}
          >
            {traslado.tipo_traslado} - {traslado.tipo_viaje}
          </Text>

          {/* Si hay vehículo asignado, mostrar en el lado derecho */}
          {traslado.nombre_vehiculo && traslado.placa && (
            <Text style={{ display: "flex", alignItems: "center" }}>
              <CarFilled
                style={{ fontSize: "16px", color: "#1890ff", marginRight: 8 }}
              />{" "}
              {traslado.nombre_vehiculo} - {traslado.placa}
            </Text>
          )}
        </div>

        {/* Modal de Observaciones */}
        <ObservacionesDialog
          open={observacionesOpen}
          onClose={handleCloseObservaciones}
          trasladoId={traslado.id}
          title={`Observaciones - CS-${traslado.id}`}
          key={`observaciones-${traslado.id}`}
        />
      </Card>
    </ColoredCard>
  );
};

// Validación de las props con PropTypes
ItemTraslado.propTypes = {
  seleccionable: PropTypes.bool,
  seleccionado: PropTypes.bool,
  onToggleSeleccion: PropTypes.func,
  traslado: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    confirmacion: PropTypes.string.isRequired,
    inicio_status: PropTypes.string,
    fin_status: PropTypes.string,
    fecha: PropTypes.string.isRequired,
    hora_recogida: PropTypes.string.isRequired,
    hora_llegada: PropTypes.string.isRequired,
    origen_direccion: PropTypes.string.isRequired,
    origen_ciudad: PropTypes.string.isRequired,
    destino_direccion: PropTypes.string.isRequired,
    destino_ciudad: PropTypes.string.isRequired,
    nombre_paciente: PropTypes.string.isRequired,
    tipo_documento_paciente: PropTypes.string.isRequired,
    numero_documento_paciente: PropTypes.string.isRequired,
    trayecto: PropTypes.string.isRequired,
    duracion_estimada: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    distancia_estimada: PropTypes.number,
    observaciones_traslado: PropTypes.string,
    indicaciones_conductor: PropTypes.string,
    empresa: PropTypes.string.isRequired,
    copago_diario: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string, // Agregar esta línea
      PropTypes.oneOf([null]),
    ]),
    nombre_vehiculo: PropTypes.string,
    placa: PropTypes.string,
    tipo_traslado: PropTypes.string.isRequired,
    tipo_viaje: PropTypes.string.isRequired,
    seguimiento: PropTypes.string,
    calendario: PropTypes.arrayOf(PropTypes.string),
    origen_lugar: PropTypes.string,
    numero_contacto_paciente: PropTypes.string,
    destino_lugar: PropTypes.string,
    autorizacion: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        numero_autorizacion: PropTypes.string,
        numero_pre_autorizacion: PropTypes.string,
        codigo_mipres: PropTypes.string,
      })
    ]),
    preautorizacion: PropTypes.string,
    observaciones_count: PropTypes.number,
    es_tai: PropTypes.bool,
    tai_trayecto_tipo: PropTypes.string,
    tai_estado_asignacion: PropTypes.string,
    transportadora_id: PropTypes.number,
    transportadora_nombre: PropTypes.string,
    es_mogotax_especial: PropTypes.bool,
  }).isRequired,
  handleContextMenu: PropTypes.func.isRequired,
  regimenes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default ItemTraslado;
